// src/components/lekhi/AudioView.js
import React, { useState, useRef, useEffect } from 'react';
import './AudioView.css';

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const AudioView = ({ audio, blockLabel, topicName, language, onComplete, onBack }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const labels = language === 'mr'
    ? { back: '← मागे', next: 'पुढे' }
    : { back: '← Back', next: 'Continue' };

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    const handleLoadedMetadata = () => setDuration(audioEl.duration);
    const handleTimeUpdate = () => setCurrentTime(audioEl.currentTime);
    const handleEnded = () => setIsPlaying(false);

    audioEl.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioEl.addEventListener('timeupdate', handleTimeUpdate);
    audioEl.addEventListener('ended', handleEnded);

    return () => {
      audioEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioEl.removeEventListener('timeupdate', handleTimeUpdate);
      audioEl.removeEventListener('ended', handleEnded);
      audioEl.pause();
    };
  }, []);

  const togglePlayPause = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    if (isPlaying) {
      audioEl.pause();
      setIsPlaying(false);
    } else {
      audioEl.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleReplay = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    audioEl.currentTime = 0;
    audioEl.play().catch(() => {});
    setIsPlaying(true);
  };

  const handleSeek = (e) => {
    const audioEl = audioRef.current;
    if (!audioEl || !duration) return;
    const newTime = (e.target.value / 100) * duration;
    audioEl.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value / 100;
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="lk-audio-container">
      <div className="lk-audio-header">
        <button className="back-btn" onClick={onBack}>{labels.back}</button>
        <div className="lk-audio-title">{blockLabel}</div>
        <div style={{ width: 60 }} />
      </div>

      <div className="lk-audio-body">
        <div className="lk-audio-card">
          <div className="lk-audio-label">{blockLabel}</div>
          {topicName && <div className="lk-audio-topic">{topicName}</div>}

          <audio ref={audioRef} src={audio?.audioURL} preload="metadata" />

          <button
            className={`lk-audio-play-btn ${isPlaying ? 'playing' : ''}`}
            onClick={togglePlayPause}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>

          <div className="lk-audio-progress-row">
            <span className="lk-audio-time">{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max="100"
              value={progressPercent}
              onChange={handleSeek}
              className="lk-audio-progress-bar"
              style={{ '--progress': `${progressPercent}%` }}
            />
            <span className="lk-audio-time">{formatTime(duration)}</span>
          </div>

          <div className="lk-audio-controls">
            <button className="lk-audio-control-btn" onClick={handleReplay} title="Replay">
              ↻
            </button>
            <div className="lk-audio-volume-wrap">
              <button
                className="lk-audio-control-btn"
                onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                title="Volume"
              >
                {volume === 0 ? '🔇' : volume < 0.5 ? '🔈' : '🔊'}
              </button>
              {showVolumeSlider && (
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume * 100}
                  onChange={handleVolumeChange}
                  className="lk-audio-volume-slider"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="lk-audio-footer">
        <button className="lk-audio-next-btn" onClick={onComplete}>{labels.next}</button>
      </div>
    </div>
  );
};

export default AudioView;